import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CampaignService } from 'src/app/_services/campaign.service';
import { LocalStorageService } from 'src/app/_services/local-storage.service';


/**
 * Interface representing the limits for an email campaign, including daily and hourly limits.
 */
export interface EmailCampaignLimits {
  /**
   * 0 = Safe, 1 = Moderate, 2 = Risky
   */
  dailyLimit: 0 | 1 | 2;
  /**
   * 0 = Safe, 1 = Moderate, 2 = Risky
   */
  hourlyLimit: 0 | 1 | 2;
}

@Component({
  selector: 'app-email-campaign-limits',
  templateUrl: './email-campaign-limits.component.html',
  styleUrls: ['./email-campaign-limits.component.scss'],
  standalone: false
})
export class EmailCampaignLimitsComponent implements OnInit {

  /** Current limits to display or edit. */
  @Input() limits!: EmailCampaignLimits;

  /**
   * Initial state of the modal:
   * - 'preview'     — shows the summary card with an Edit button.
   * - 'edit'        — opens directly on the level selector.
   * - 'acknowledge' — informs the user that limits are active; offers Edit or Close.
   */
  @Input() mode: 'preview' | 'edit' | 'acknowledge' = 'preview';

  /** Tracks which view is currently rendered inside the modal. */
  currentMode: 'preview' | 'edit' | 'acknowledge' = 'preview';

  /** Holds the user's pending selection while in edit mode. */
  pendingLevel: number = 0;

  /** True while the API save is in-flight. */
  saving: boolean = false;

  readonly levelConfig = [
    {
      key: 'safe',
      label: 'Safe',
      daily: '≤ 60',
      hourly: '≤ 10',
      note: 'Ideal for warming up new inboxes. Closely mimics natural sending patterns.',
    },
    {
      key: 'moderate',
      label: 'Moderate',
      daily: '61–150',
      hourly: '11–20',
      note: 'Suitable for established accounts with a healthy sender reputation.',
    },
    {
      key: 'risky',
      label: 'Risky',
      daily: '151–400',
      hourly: '21–35',
      note: 'High-volume sending. Risk of spam flags if inbox reputation is below average.',
    },
  ];

  constructor(
    private ngbModal: NgbActiveModal,
    private campaignService: CampaignService,
    private localStorageService: LocalStorageService,
  ) { }

  ngOnInit(): void {
    if (!this.limits) {
      this.limits = { dailyLimit: 0, hourlyLimit: 0 };
    }
    this.currentMode = this.mode;
    this.pendingLevel = this.activeLevel;
    const usernumber = this.localStorageService.getItem('usernumber') || '';
    this.fetchUserLimits(usernumber);
  }

  // ── Computed display values ──────────────────────────────────

  get activeLevel(): number {
    return Math.min(this.limits.dailyLimit, this.levelConfig.length - 1);
  }

  get activeLevelLabel(): string {
    return this.levelConfig[this.activeLevel].label;
  }

  get activeLevelKey(): string {
    return this.levelConfig[this.activeLevel].key;
  }

  get activeDailyDisplay(): string {
    return this.levelConfig[this.activeLevel].daily;
  }

  get activeHourlyDisplay(): string {
    return this.levelConfig[this.activeLevel].hourly;
  }

  get activeLevelNote(): string {
    return this.levelConfig[this.activeLevel].note;
  }

  // ── Data fetching ────────────────────────────────────────────

  private fetchUserLimits(usernumber: string): void {
    if (!usernumber) return;
    this.campaignService.fetchUserLimits(usernumber).subscribe((res: any) => {
      if (res && res.data) {
        const daily = res.data.email_limit_per_day != null ? res.data.email_limit_per_day : 0;
        const hourly = res.data.email_limit_per_hour != null ? res.data.email_limit_per_hour : 0;
        this.limits = {
          dailyLimit: Math.min(daily, 2) as 0 | 1 | 2,
          hourlyLimit: Math.min(hourly, 2) as 0 | 1 | 2,
        };
        this.pendingLevel = this.activeLevel;
      }
    }, (_err) => {});
  }

  // ── Mode switching ───────────────────────────────────────────

  switchToEdit(): void {
    this.pendingLevel = this.activeLevel;
    this.currentMode = 'edit';
  }

  cancelEdit(): void {
    if (this.mode === 'edit') {
      // Opened directly in edit mode — dismiss the whole modal.
      this.ngbModal.dismiss('cancel');
    } else if (this.mode === 'acknowledge') {
      // Came from acknowledge — go back to acknowledge.
      this.currentMode = 'acknowledge';
    } else {
      // Came from preview — go back to preview.
      this.currentMode = 'preview';
    }
  }

  // ── Save / dismiss ───────────────────────────────────────────

  saveChanges(): void {
    const usernumber = this.localStorageService.getItem('usernumber') || '';
    const payload = {
      usernumber,
      dailyLimit: this.pendingLevel,
      hourlyLimit: this.pendingLevel,
    };
    this.saving = true;
    this.campaignService.updateUserLimits(payload).subscribe((_res: any) => {
      this.saving = false;
      const newLimits: EmailCampaignLimits = {
        dailyLimit: this.pendingLevel as 0 | 1 | 2,
        hourlyLimit: this.pendingLevel as 0 | 1 | 2,
      };
      this.ngbModal.close(newLimits);
    }, (_err) => {
      this.saving = false;
      const newLimits: EmailCampaignLimits = {
        dailyLimit: this.pendingLevel as 0 | 1 | 2,
        hourlyLimit: this.pendingLevel as 0 | 1 | 2,
      };
      this.ngbModal.close(newLimits);
    });
  }

  acknowledge(): void {
    this.ngbModal.dismiss('acknowledged');
  }

  dismiss(reason?: any): void {
    this.ngbModal.dismiss(reason);
  }

}
